import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider, getToken as getAppCheckToken } from 'firebase/app-check';
import { startAnalytics } from './analytics.mjs';

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const element = (tag, text, className) => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className) node.className = className;
  return node;
};
const button = (text, className = 'button button-outline') => {
  const node = element('button', text, className);
  node.type = 'button';
  return node;
};
const status = (node, message, error = false) => {
  if (!node) return;
  node.textContent = message;
  node.classList.toggle('error', error);
  node.dataset.state = error ? 'error' : 'success';
};
let toastTimer;
function toast(message, error = false) {
  const node = $('#toast');
  if (!node) return;
  clearTimeout(toastTimer);
  status(node, message, error);
  node.classList.add('visible', 'show');
  toastTimer = setTimeout(() => node.classList.remove('visible', 'show'), 6500);
}

const dialogFocus = new WeakMap();
function openDialog(dialog) {
  if (!dialog || dialog.open) return;
  dialogFocus.set(dialog, document.activeElement);
  dialog.showModal();
}
function connectDialog(dialog) {
  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-close]')) dialog.close();
    else if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
  });
  dialog.addEventListener('close', () => {
    const previous = dialogFocus.get(dialog);
    if (previous?.isConnected && !$$('dialog[open]').length) previous.focus();
  });
}
$$('dialog').forEach(connectDialog);

const menuToggle = $('.menu-toggle');
const header = $('.site-header');
const navigation = $('.site-header nav');
if (menuToggle && navigation) {
  navigation.id ||= 'main-navigation';
  menuToggle.setAttribute('aria-controls', navigation.id);
  const closeMenu = () => {
    menuToggle.setAttribute('aria-expanded', 'false');
    header.classList.remove('menu-open');
  };
  menuToggle.addEventListener('click', () => {
    const expanded = menuToggle.getAttribute('aria-expanded') !== 'true';
    menuToggle.setAttribute('aria-expanded', String(expanded));
    header.classList.toggle('menu-open', expanded);
  });
  navigation.addEventListener('click', event => { if (event.target.closest('a')) closeMenu(); });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && menuToggle.getAttribute('aria-expanded') === 'true') {
      closeMenu();
      menuToggle.focus();
    }
  });
  document.addEventListener('click', event => { if (!header.contains(event.target)) closeMenu(); });
}

// Bookmarks stay on this device. Only update the UI after storage accepts the write.
const bookmarkKey = 'gamepulse.saved.v1';
let saved = new Set();
function readSaved() {
  try {
    const parsed = JSON.parse(localStorage.getItem(bookmarkKey) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,140}$/.test(id)).slice(0, 5000) : []);
  } catch { return new Set(); }
}
function paintSaved() {
  $$('[data-save]').forEach(node => {
    const active = saved.has(node.dataset.save);
    node.setAttribute('aria-pressed', String(active));
    node.classList.toggle('is-saved', active);
    node.title = active ? 'Remove from saved games' : 'Save on this device';
  });
  let visible = 0;
  $$('[data-saved-card]').forEach(node => {
    node.hidden = !saved.has(node.dataset.savedCard);
    if (!node.hidden) visible += 1;
  });
  if ($('#saved-count')) $('#saved-count').textContent = String(saved.size);
  if ($('#saved-grid') && $('#saved-empty')) $('#saved-empty').hidden = saved.size > 0;
}
saved = readSaved();
paintSaved();
document.addEventListener('click', event => {
  const node = event.target.closest('[data-save]');
  if (!node) return;
  const next = readSaved();
  const removing = next.has(node.dataset.save);
  if (removing) next.delete(node.dataset.save); else next.add(node.dataset.save);
  try {
    localStorage.setItem(bookmarkKey, JSON.stringify([...next]));
    saved = next;
    paintSaved();
    toast(removing ? 'Removed from your saved games.' : 'Saved on this device. Find it in your bookmarks.');
  } catch { toast('Your browser could not save this bookmark. Allow site storage and try again.', true); }
});
window.addEventListener('storage', event => {
  if (event.key === bookmarkKey || event.key === null) { saved = readSaved(); paintSaved(); void loadSaved(true); }
});


// Catalog reads are bounded and public. Never send authentication for discovery.
async function publicCatalog(path) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(path, { credentials: 'omit', headers: { Accept: 'application/json' }, referrerPolicy: 'no-referrer', signal: controller.signal });
    const result = await response.json();
    if (!response.ok) throw new Error(typeof result.error === 'string' ? result.error : 'Could not load the game catalog.');
    if (!Array.isArray(result.games) || result.games.length > 50) throw new Error('The catalog returned an unexpected response.');
    return result.games.filter(game => game && typeof game.id === 'string' && /^[a-zA-Z0-9_-]{1,140}$/.test(game.id) && typeof game.name === 'string' && /^[a-zA-Z0-9_-]{1,240}$/.test(game.slug || ''));
  } finally { clearTimeout(timeout); }
}
function catalogImage(url, alt, className = '') {
  const fallback = element('span', 'GG', `image-placeholder ${className}`);
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return fallback;
    const img = element('img', undefined, className);
    img.src = parsed.href; img.alt = alt; img.loading = 'lazy'; img.decoding = 'async'; img.referrerPolicy = 'no-referrer';
    return img;
  } catch { return fallback; }
}
function savedCard(id, game, index) {
  const wrapper = element('div'); wrapper.dataset.savedCard = id;
  const card = element('article', undefined, 'game-card'); wrapper.append(card);
  const content = element('div', undefined, 'card-content');
  if (game) {
    const visual = element('a', undefined, `card-visual tone-${index % 4}`); visual.href = `/games/${encodeURIComponent(game.slug)}`;
    visual.append(catalogImage(game.landscape?.[0] || game.screenshots?.[0] || game.icon, `${game.name} — official game image`), element('span', game.distribution === 'web' ? 'Web' : game.provider === 'Steam' ? 'PC' : game.steam ? 'iOS + PC' : 'iOS', 'platform-badge')); card.append(visual);
  }
  const top = element('div', undefined, 'card-top'), identity = element(game ? 'a' : 'div', undefined, 'game-identity');
  if (game) { identity.href = `/games/${encodeURIComponent(game.slug)}`; identity.append(catalogImage(game.icon, `${game.name} app icon`, 'app-icon')); }
  const title = element('span'); title.append(element('h3', game?.name || 'Listing currently unavailable'), element('small', game?.developer || 'Your bookmark is still saved on this device.')); identity.append(title);
  const save = button('✓', 'icon-button save-button'); save.dataset.save = id; save.setAttribute('aria-label', `Remove ${game?.name || 'unavailable game'} from saved games`);
  top.append(identity, save); content.append(top);
  if (game) {
    content.append(element('p', String(game.editorialSummary || game.description || 'Explore official game information, screenshots, and community discussions.').replace(/\s+/g, ' ').slice(0, 110), 'card-description'));
    const tags = element('div', undefined, 'tag-row');
    for (const tag of [...new Set([...(Array.isArray(game.tags) ? game.tags : []), ...(Array.isArray(game.genres) ? game.genres : [])])].slice(0, 2)) tags.append(element('span', String(tag), 'tag'));
    content.append(tags);
    const detail = element('a', 'View game →', 'text-link'); detail.href = `/games/${encodeURIComponent(game.slug)}`; content.append(detail);
  }
  card.append(content); return wrapper;
}
const savedGrid = $('#saved-grid'), savedMore = $('#saved-more'), savedStatus = $('#saved-status');
let savedIds = [], savedOffset = 0, savedGeneration = 0, savedLoading = false;
async function loadSaved(reset = false) {
  if (!savedGrid) return;
  if (reset) { savedGeneration++; savedIds = [...saved]; savedOffset = 0; savedGrid.replaceChildren(); savedLoading = false; }
  if (savedLoading) return;
  const generation = savedGeneration;
  const batch = savedIds.slice(savedOffset, savedOffset + 50);
  if (!batch.length) { savedMore.hidden = true; status(savedStatus, ''); paintSaved(); return; }
  savedLoading = true; savedMore.disabled = true; savedMore.hidden = true; status(savedStatus, 'Loading your saved games…');
  try {
    const games = await publicCatalog(`/api/games?${new URLSearchParams({ ids: batch.join(',') })}`);
    if (generation !== savedGeneration) return;
    const byId = new Map(games.map(game => [game.id, game]));
    const fragment = document.createDocumentFragment();
    batch.forEach((id, index) => { if (saved.has(id)) fragment.append(savedCard(id, byId.get(id), savedOffset + index)); });
    savedGrid.append(fragment); savedOffset += batch.length;
    status(savedStatus, `Loaded ${savedOffset} of ${savedIds.length} saved bookmarks. Unavailable listings stay saved until you remove them.`);
    savedMore.hidden = savedOffset >= savedIds.length;
    savedMore.textContent = 'Load more saved games'; paintSaved();
  } catch (error) {
    if (generation !== savedGeneration) return;
    status(savedStatus, 'Could not load saved games. Your bookmarks are still on this device. Please try again.', true);
    savedMore.hidden = false; savedMore.textContent = 'Try again';
  } finally { if (generation === savedGeneration) { savedLoading = false; savedMore.disabled = false; } }
}
savedMore?.addEventListener('click', () => { void loadSaved(); });
void loadSaved(true);

const lightbox = $('#lightbox');
const screenshots = $$('[data-lightbox]');
let screenshotIndex = 0;
if (lightbox && screenshots.length) {
  lightbox.setAttribute('aria-label', 'Official game screenshots');
  const controls = element('div', undefined, 'lightbox-controls');
  const previous = button('← Previous');
  const next = button('Next →');
  controls.append(previous, next);
  controls.hidden = screenshots.length < 2;
  lightbox.append(controls);
  function showScreenshot(index) {
    screenshotIndex = (index + screenshots.length) % screenshots.length;
    const source = screenshots[screenshotIndex];
    let url;
    try { url = new URL(source.dataset.lightbox, location.href); } catch { return; }
    if (!['http:', 'https:'].includes(url.protocol)) return;
    const img = $('img', lightbox);
    img.src = url.href;
    img.alt = source.dataset.caption || 'Official game screenshot';
    $('p', lightbox).textContent = `${img.alt} · ${screenshotIndex + 1} of ${screenshots.length}`;
    openDialog(lightbox);
  }
  screenshots.forEach((node, index) => node.addEventListener('click', () => showScreenshot(index)));
  previous.addEventListener('click', () => showScreenshot(screenshotIndex - 1));
  next.addEventListener('click', () => showScreenshot(screenshotIndex + 1));
  lightbox.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      showScreenshot(screenshotIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }
  });
}

let config = {};
try { config = JSON.parse($('#app-config')?.textContent || '{}'); } catch { /* Invalid config must never enable simulated sign-in. */ }
try { startAnalytics(config.metrics); } catch { /* Anonymous counts must never interrupt browsing or authentication. */ }
const authDialog = $('#auth-dialog');
const authStatus = $('#auth-status');
const signInButton = $('#google-signin');
const signOutButton = $('#signout');
const composeDialog = $('#compose-dialog');
const composeForm = $('#compose-form');
authDialog?.setAttribute('aria-label', 'Your Good Game Daily account');
composeDialog?.setAttribute('aria-label', 'Start a game discussion');
const profilePanel = element('section', undefined, 'account-panel');
profilePanel.hidden = true;
profilePanel.setAttribute('aria-label', 'Your account');
authDialog?.append(profilePanel);
const profileName = element('h3');
const profileAllowance = element('p');
const accountLink = element('a', 'Your community submissions', 'text-link');
accountLink.href = '/community#my-topics';
const adminLink = element('a', 'Open moderation workspace', 'text-link');
adminLink.href = '/admin';
adminLink.hidden = true;
const deleteButton = button('Delete my account and community content', 'text-link account-delete');
profilePanel.append(profileName, profileAllowance, accountLink, element('br'), adminLink, element('br'), deleteButton);

let auth = null;
let appCheck = null;
let currentUser = null;
let me = null;
let authGeneration = 0;
let pendingCompose = null;
let initializationError = '';
const unavailableMessage = config.preview
  ? 'Sign-in and community publishing are unavailable in this local preview. They require the connected Firebase deployment.'
  : 'Sign-in is not configured for this deployment yet. Please try again after the service is connected.';

function readableError(error) {
  const messages = {
    'auth/popup-closed-by-user': 'Sign-in was canceled. You can try again whenever you are ready.',
    'auth/cancelled-popup-request': 'Another sign-in request is open. Complete it or try again.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow popups for this site and try again.',
    'auth/network-request-failed': 'Could not connect to Google sign-in. Check your connection and try again.',
    'auth/unauthorized-domain': 'Google sign-in is not enabled for this website address yet.',
    'auth/operation-not-allowed': 'Google sign-in is not enabled for this deployment yet.',
    'auth/user-disabled': 'This account is disabled. Sign-in could not be completed.',
  };
  if (messages[error?.code]) return messages[error.code];
  if (error?.name === 'AbortError') return 'The request timed out. Check your submissions before trying again.';
  if (error instanceof TypeError) return 'Could not reach the service. Check your connection and try again.';
  return error?.message || 'Something went wrong. Please try again.';
}
async function api(path, { method = 'GET', body } = {}) {
  if (!auth) throw new Error(initializationError || unavailableMessage);
  const user = currentUser;
  if (!user) throw new Error('Sign in with Google to continue.');
  const headers = { Authorization: `Bearer ${await user.getIdToken()}`, Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (appCheck) {
    try { headers['X-Firebase-AppCheck'] = (await getAppCheckToken(appCheck, false)).token; }
    catch { throw new Error('App verification failed. Reload this page and try again.'); }
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 25000);
  try {
    const response = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
    let result;
    try { result = await response.json(); } catch { throw new Error('The service returned an unexpected response. Please try again.'); }
    if (!response.ok) {
      const error = new Error(typeof result.error === 'string' ? result.error : 'The request could not be completed.');
      error.status = response.status;
      throw error;
    }
    return result;
  } finally { clearTimeout(timeout); }
}
function paintAccount() {
  const signedIn = Boolean(currentUser);
  if (signInButton) signInButton.hidden = signedIn;
  if (signOutButton) signOutButton.hidden = !signedIn;
  profilePanel.hidden = !signedIn;
  profileName.textContent = currentUser?.displayName || 'Player';
  profileAllowance.textContent = !me ? 'Loading your daily allowance…' : me.canPost
    ? 'You can start one topic today. Replies are always welcome.'
    : 'Your next new topic opens at 00:00 UTC. Replies are always welcome.';
  adminLink.hidden = !me?.admin;
  $$('[data-auth]').forEach(node => { node.textContent = signedIn ? 'Your account' : node.closest('.header-actions') ? 'Join the conversation →' : 'Sign in'; });
  const topicPanel = $('#my-topics');
  const list = $('#my-topic-list');
  if (topicPanel && list) {
    topicPanel.hidden = !signedIn;
    list.replaceChildren();
    if (me?.topics?.length) {
      for (const topic of me.topics) {
        const row = element('article', undefined, 'submission-row');
        const heading = element(topic.status === 'published' ? 'a' : 'strong', topic.title);
        if (topic.status === 'published') heading.href = `/community/${encodeURIComponent(topic.id)}`;
        row.append(heading, element('p', `Status: ${topic.status}${topic.reason ? ` · ${topic.reason}` : ''}`));
        list.append(row);
      }
    } else if (me) list.append(element('p', 'You have not submitted any topics yet.', 'muted'));
  }
}
async function refreshMe() {
  const generation = authGeneration;
  const result = await api('/api/me');
  if (generation !== authGeneration || !currentUser) return null;
  me = result;
  paintAccount();
  return result;
}
function openAuth(message = '') {
  status(authStatus, auth ? message : initializationError || unavailableMessage, !auth);
  openDialog(authDialog);
}
function needsSignIn() {
  if (currentUser) return false;
  openAuth('Sign in to join the conversation.');
  return true;
}
const gameSearch = $('#compose-game-search'), gameFind = $('#compose-game-find'), gameSearchStatus = $('#compose-game-status');
let gameSearchGeneration = 0;
function gameOption(game) { const option = element('option', game.name); option.value = game.id; return option; }
async function searchComposeGames() {
  const query = gameSearch.value.trim(), generation = ++gameSearchGeneration;
  if (query.length < 2) { gameFind.disabled = false; status(gameSearchStatus, 'Enter at least two characters from the start of a game title.', true); return; }
  gameFind.disabled = true; status(gameSearchStatus, 'Finding games…');
  try {
    const games = await publicCatalog(`/api/catalog?${new URLSearchParams({ q: query })}`);
    if (generation !== gameSearchGeneration) return;
    const select = composeForm.elements.gameId, current = select.selectedOptions[0];
    const placeholder = element('option', 'Choose a game'); placeholder.value = '';
    select.replaceChildren(placeholder);
    if (current?.value && !games.some(game => game.id === current.value)) select.append(current);
    games.slice(0, 24).forEach(game => select.append(gameOption(game)));
    select.value = current?.value || '';
    status(gameSearchStatus, games.length ? `Found ${games.length} matches. Choose a game below; refine the title prefix for more specific results.` : 'No matches for that title prefix. Try the beginning of a different title.');
  } catch (error) { if (generation === gameSearchGeneration) status(gameSearchStatus, readableError(error), true); }
  finally { if (generation === gameSearchGeneration) gameFind.disabled = false; }
}
gameFind?.addEventListener('click', () => { void searchComposeGames(); });
gameSearch?.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); void searchComposeGames(); } });
async function showComposer(gameId = '') {
  if (!currentUser) {
    pendingCompose = gameId;
    openAuth('Sign in to start your daily discussion.');
    return;
  }
  status($('.form-status', composeForm), me?.canPost === false ? 'You have already submitted today’s topic. Your next topic opens at 00:00 UTC.' : '', me?.canPost === false);
  openDialog(composeDialog);
  if (gameId && composeForm) {
    const select = composeForm.elements.gameId;
    if (![...select.options].some(option => option.value === gameId)) {
      const generation = ++gameSearchGeneration;
      select.value = ''; status(gameSearchStatus, 'Loading the selected game…');
      try {
        const games = await publicCatalog(`/api/games?${new URLSearchParams({ ids: gameId })}`);
        if (generation !== gameSearchGeneration) return;
        const game = games.find(game => game.id === gameId);
        if (!game) throw new Error('This game is no longer available. Please choose another game.');
        select.append(gameOption(game)); status(gameSearchStatus, '');
      } catch (error) { if (generation === gameSearchGeneration) status(gameSearchStatus, readableError(error), true); return; }
      finally { if (generation === gameSearchGeneration) gameFind.disabled = false; }
    }
    select.value = gameId;
  }
}
$$('[data-auth]').forEach(node => node.addEventListener('click', () => { pendingCompose = null; openAuth(); }));
$$('[data-compose]').forEach(node => node.addEventListener('click', () => showComposer(node.dataset.game || '')));
authDialog?.addEventListener('close', () => { if (!currentUser) pendingCompose = null; });

if (config.firebase?.apiKey && config.firebase?.projectId && config.firebase?.authDomain) {
  try {
    const app = initializeApp(config.firebase);
    auth = getAuth(app);
    if (config.appCheckSiteKey) appCheck = initializeAppCheck(app, { provider: new ReCaptchaV3Provider(config.appCheckSiteKey), isTokenAutoRefreshEnabled: true });
    onAuthStateChanged(auth, user => {
      currentUser = user;
      me = null;
      authGeneration += 1;
      // A previous moderator's private queue must disappear on every account change.
      $('#admin-queue')?.replaceChildren();
      $('#admin-analytics')?.replaceChildren();
      if ($('#analytics-status')) status($('#analytics-status'), user ? 'Load anonymous page counts to check your moderator access.' : 'Sign in with a moderator account to view site counts.');
      if ($('#admin-status')) status($('#admin-status'), user ? 'Load the queue to check your moderator access.' : 'Sign in with a moderator account to load the queue.');
      paintAccount();
      if (user) {
        status(authStatus, `Signed in as ${user.displayName || 'Player'}.`);
        refreshMe().catch(error => {
          status(authStatus, readableError(error), true);
          profileAllowance.textContent = 'Your allowance could not be loaded. The server will check it when you submit.';
        });
        if (pendingCompose !== null) {
          const gameId = pendingCompose;
          pendingCompose = null;
          authDialog?.close();
          showComposer(gameId);
        }
      }
    }, error => status(authStatus, readableError(error), true));
  } catch (error) {
    auth = null;
    initializationError = 'Sign-in could not be initialized for this deployment. Please reload and try again.';
  }
}
signInButton?.addEventListener('click', async () => {
  if (!auth) { status(authStatus, initializationError || unavailableMessage, true); return; }
  signInButton.disabled = true;
  status(authStatus, 'Opening Google sign-in…');
  try { await signInWithPopup(auth, new GoogleAuthProvider()); }
  catch (error) { status(authStatus, readableError(error), true); }
  finally { signInButton.disabled = false; }
});
signOutButton?.addEventListener('click', async () => {
  if (!auth) return;
  signOutButton.disabled = true;
  try { await signOut(auth); status(authStatus, 'You are signed out.'); }
  catch (error) { status(authStatus, readableError(error), true); }
  finally { signOutButton.disabled = false; }
});
deleteButton.addEventListener('click', async () => {
  if (!currentUser || !window.confirm('Request permanent deletion of your Good Game Daily account and all your topics, replies, votes, and reports? This cannot be undone. Your local game bookmarks will remain on this device.')) return;
  deleteButton.disabled = true;
  status(authStatus, 'Requesting account deletion…');
  try {
    const result = await api('/api/me', { method: 'DELETE' });
    status(authStatus, result.message || 'Account deletion requested. Cleanup will run in the background.');
    await signOut(auth).catch(() => {});
    toast('Account deletion requested. Your content will be removed by the cleanup job.');
  } catch (error) { status(authStatus, readableError(error), true); }
  finally { deleteButton.disabled = false; }
});

async function formRequest(form, work) {
  if (form.dataset.submitting === 'true' || !form.reportValidity()) return;
  if (needsSignIn()) return;
  form.dataset.submitting = 'true';
  const submitButtons = $$('[type="submit"]', form);
  submitButtons.forEach(node => { node.disabled = true; });
  status($('.form-status', form), 'Submitting…');
  try { await work(); }
  catch (error) { status($('.form-status', form), readableError(error), true); }
  finally {
    delete form.dataset.submitting;
    submitButtons.forEach(node => { node.disabled = false; });
  }
}
composeForm?.addEventListener('submit', event => {
  event.preventDefault();
  void formRequest(composeForm, async () => {
    const result = await api('/api/threads', { method: 'POST', body: Object.fromEntries(new FormData(composeForm)) });
    composeForm.reset();
    status($('.form-status', composeForm), result.message);
    toast('Your topic was submitted for review. It is not public yet.');
    if (me) { me.canPost = false; paintAccount(); }
    await refreshMe().catch(() => {});
  });
});
const replyForm = $('#reply-form');
replyForm?.addEventListener('submit', event => {
  event.preventDefault();
  void formRequest(replyForm, async () => {
    const result = await api(`/api/threads/${encodeURIComponent(replyForm.dataset.thread)}/replies`, { method: 'POST', body: Object.fromEntries(new FormData(replyForm)) });
    replyForm.reset();
    status($('.form-status', replyForm), result.message);
    toast('Your reply was submitted for review. It is not public yet.');
  });
});
$$('[data-vote]').forEach(node => node.addEventListener('click', async () => {
  if (needsSignIn() || node.disabled) return;
  const controls = $$('[data-vote]').filter(other => other.dataset.vote === node.dataset.vote);
  controls.forEach(other => { other.disabled = true; });
  try {
    const result = await api(`/api/threads/${encodeURIComponent(node.dataset.vote)}/vote`, { method: 'POST', body: {} });
    controls.forEach(other => {
      other.setAttribute('aria-pressed', String(result.voted));
      other.classList.toggle('is-voted', result.voted);
      if ($('span', other)) $('span', other).textContent = String(result.votes);
    });
    toast(result.voted ? 'Marked as useful.' : 'Your vote was removed.');
  } catch (error) { toast(readableError(error), true); }
  finally { controls.forEach(other => { other.disabled = false; }); }
}));

// Build reports with text nodes so topic titles and service responses never become HTML.
const reportDialog = element('dialog', undefined, 'modal');
reportDialog.setAttribute('aria-label', 'Report content');
const closeReport = button('×', 'icon-button modal-close');
closeReport.dataset.close = '';
closeReport.setAttribute('aria-label', 'Close report');
const reportForm = element('form');
const reportLabel = element('label', 'What should the moderators review?');
const reportReason = element('textarea');
reportReason.name = 'reason'; reportReason.required = true; reportReason.minLength = 5; reportReason.maxLength = 1000; reportReason.rows = 4;
reportLabel.append(reportReason);
const submitReport = button('Send report', 'button button-lime'); submitReport.type = 'submit';
const reportStatus = element('p', undefined, 'form-status'); reportStatus.setAttribute('role', 'status');
reportForm.append(reportLabel, submitReport, reportStatus);
reportDialog.append(closeReport, element('h2', 'Help keep the community good.'), reportForm);
document.body.append(reportDialog);
connectDialog(reportDialog);
let reportTarget = null;
$$('[data-report]').forEach(node => node.addEventListener('click', () => {
  if (needsSignIn()) return;
  reportTarget = { threadId: node.dataset.report, replyId: node.dataset.reply || null };
  reportForm.reset(); status(reportStatus, '');
  openDialog(reportDialog);
}));
reportForm.addEventListener('submit', event => {
  event.preventDefault();
  void formRequest(reportForm, async () => {
    const result = await api(`/api/threads/${encodeURIComponent(reportTarget.threadId)}/report`, { method: 'POST', body: { reason: reportReason.value, replyId: reportTarget.replyId } });
    reportDialog.close();
    toast(result.message);
  });
});

const adminStatus = $('#admin-status');
const adminQueue = $('#admin-queue');
const loadAdmin = $('#load-admin');
function moderationCard(item, kind) {
  const card = element('article', undefined, 'signal-panel moderation-card');
  const heading = kind === 'topics' ? item.title : kind === 'replies' ? 'Reply awaiting review' : 'Reported content';
  card.append(element('h3', heading));
  if (item.authorName) card.append(element('small', `By ${item.authorName}`));
  if (item.body) card.append(element('p', item.body, 'moderation-body'));
  if (kind === 'reports') card.append(element('p', item.reason, 'moderation-body'));
  const threadId = kind === 'topics' ? item.id : item.threadId;
  const replyId = kind === 'replies' ? item.id : item.replyId || null;
  const context = element('a', 'View discussion ↗', 'text-link');
  context.href = `/community/${encodeURIComponent(threadId)}`;
  context.target = '_blank'; context.rel = 'noopener noreferrer';
  card.append(context);
  const actions = element('div', undefined, 'moderation-actions');
  const feedback = element('p', undefined, 'form-status'); feedback.setAttribute('role', 'status');
  async function takeAction(action, work) {
    const buttons = $$('button', card);
    buttons.forEach(node => { node.disabled = true; });
    status(feedback, `${action}…`);
    try { await work(); card.remove(); status(adminStatus, `${action} completed. Reload the queue for the latest submissions.`); }
    catch (error) { status(feedback, readableError(error), true); buttons.forEach(node => { node.disabled = false; }); }
  }
  function addAction(label, actionStatus, className) {
    const control = button(label, className);
    control.addEventListener('click', () => {
      const reason = actionStatus === 'published' ? '' : window.prompt('Optional moderation reason (shown to the author):', '');
      if (reason === null) return;
      void takeAction(label, () => api('/api/admin/moderate', { method: 'POST', body: { threadId, replyId, status: actionStatus, reason, ...(kind === 'reports' ? { reportId: item.id } : {}) } }));
    });
    actions.append(control);
  }
  if (kind === 'reports') {
    addAction('Hide reported content', 'hidden', 'button button-outline');
    const dismiss = button('Resolve report');
    dismiss.addEventListener('click', () => void takeAction('Resolve report', () => api(`/api/admin/reports/${encodeURIComponent(item.id)}/resolve`, { method: 'POST', body: {} })));
    actions.append(dismiss);
  } else {
    addAction('Approve', 'published', 'button button-lime');
    addAction('Reject', 'rejected', 'button button-outline');
  }
  card.append(actions, feedback);
  return card;
}
async function fetchAdminQueue() {
  if (!adminQueue || needsSignIn()) return;
  if (loadAdmin) loadAdmin.disabled = true;
  status(adminStatus, 'Loading moderation queue…');
  try {
    const queue = await api('/api/admin/queue');
    adminQueue.replaceChildren();
    let total = 0;
    for (const [key, label] of [['topics', 'Topics'], ['replies', 'Replies'], ['reports', 'Reports']]) {
      const items = Array.isArray(queue[key]) ? queue[key] : [];
      total += items.length;
      const section = element('section', undefined, 'moderation-section');
      section.append(element('h2', `${label} · ${items.length}`));
      if (!items.length) section.append(element('p', `No pending ${label.toLowerCase()}.`, 'muted'));
      for (const item of items) section.append(moderationCard(item, key));
      adminQueue.append(section);
    }
    status(adminStatus, `${total} items loaded. Each queue shows up to 100 items.`);
  } catch (error) {
    adminQueue.replaceChildren();
    status(adminStatus, readableError(error), true);
  } finally { if (loadAdmin) loadAdmin.disabled = false; }
}
loadAdmin?.addEventListener('click', () => void fetchAdminQueue());
const loadAnalytics = $('#load-analytics');
const analyticsStatus = $('#analytics-status');
const analyticsPanel = $('#admin-analytics');
loadAnalytics?.addEventListener('click', async () => {
  if (!analyticsPanel || needsSignIn() || loadAnalytics.disabled) return;
  const generation = authGeneration;
  loadAnalytics.disabled = true;
  status(analyticsStatus, 'Loading anonymous site counts…');
  try {
    const report = await api('/api/admin/analytics');
    if (generation !== authGeneration || !currentUser) return;
    const count = value => {
      if (!Number.isSafeInteger(value) || value < 0) throw new Error('The site-count report returned an invalid total.');
      return new Intl.NumberFormat('en').format(value);
    };
    if (!Array.isArray(report.daily) || !Array.isArray(report.topGames) || !report.window || !/^\d{4}-\d{2}-\d{2}$/.test(report.window.from || '') || !/^\d{4}-\d{2}-\d{2}$/.test(report.window.to || '')) throw new Error('The site-count report returned an unexpected response.');
    const fragment = document.createDocumentFragment();
    const totals = element('div', undefined, 'metric-totals');
    for (const [label, value] of [['Page views', report.totals?.pageViews], ['Game-page views', report.totals?.gameViews]]) {
      const item = element('p', label); item.prepend(element('strong', count(value))); totals.append(item);
    }
    fragment.append(totals, element('p', `${report.window.from} through ${report.window.to} · UTC. Accepted page-load signals include repeat visits; they do not measure unique visitors or every visit.`, 'muted'));
    function table(title, headings, rows) {
      const result = element('table', undefined, 'metric-table');
      result.append(element('caption', title));
      const head = element('thead'), header = element('tr');
      for (const label of headings) { const cell = element('th', label); cell.scope = 'col'; header.append(cell); }
      head.append(header); result.append(head);
      const body = element('tbody');
      for (const row of rows) { const tr = element('tr'); for (const value of row) { const cell = element('td'); if (typeof value === 'string') cell.textContent = value; else cell.append(value); tr.append(cell); } body.append(tr); }
      result.append(body); return result;
    }
    const dailyRows = report.daily.slice(0, 90).sort((a, b) => String(b.date).localeCompare(String(a.date))).map(day => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day.date || '')) throw new Error('The site-count report returned an invalid date.');
      return [day.date, count(day.pageViews), count(day.gameViews)];
    });
    fragment.append(table('Daily page loads', ['Date (UTC)', 'Page views', 'Game-page views'], dailyRows));
    if (!dailyRows.length) fragment.append(element('p', 'No daily counts have been recorded in this window.', 'muted'));
    const gameRows = report.topGames.slice(0, 50).map(game => {
      const label = String(game.name || `App Store game ${game.id}`);
      const name = /^[A-Za-z0-9_-]+$/.test(game.slug || '') ? element('a', label) : element('span', label);
      if (name.tagName === 'A') name.href = `/games/${encodeURIComponent(game.slug)}`;
      return [name, count(game.views)];
    });
    fragment.append(table('Popular game pages on this site', ['Game', 'Page views'], gameRows));
    if (!gameRows.length) fragment.append(element('p', 'No game-page counts have been recorded in this window.', 'muted'));
    if (report.coverage?.truncated) fragment.append(element('p', 'Popular-game coverage is partial: some game-page counts are outside this report. Daily totals remain separate.', 'muted'));
    analyticsPanel.replaceChildren(fragment);
    status(analyticsStatus, 'Anonymous aggregate report loaded. Social Pulse is a separate measure.');
  } catch (error) {
    if (generation !== authGeneration) return;
    analyticsPanel.replaceChildren(); status(analyticsStatus, readableError(error), true);
  } finally { loadAnalytics.disabled = false; }
});
const upcomingForm = $('#upcoming-form');
upcomingForm?.addEventListener('submit', event => {
  event.preventDefault();
  void formRequest(upcomingForm, async () => {
    const result = await api('/api/admin/upcoming', { method: 'POST', body: Object.fromEntries(new FormData(upcomingForm)) });
    upcomingForm.reset();
    const feedback = $('.form-status', upcomingForm);
    status(feedback, 'The verified upcoming game was published. ');
    if (typeof result.url === 'string' && /^\/games\/[a-zA-Z0-9_-]+$/.test(result.url)) {
      const link = element('a', 'View game →'); link.href = result.url; feedback.append(link);
    }
  });
});
